import * as Comlink from "comlink";
import { getBaseMonth, toAbsoluteOffset, toRelativeOffset } from "./convert.js";
import { DeploymentStore } from "./deploymentStore.js";
import { EodStore } from "./eodStore.js";
import { LayoutStore } from "./layoutStore.js";
import { normalize } from "./normalize.js";
import { validateOffset } from "./offset.js";
import { createPagesApi } from "./pagesApi.js";
import { PagesStore } from "./pagesStore.js";
import { ProductCatalogStore } from "./productCatalogStore.js";
import { WatcherRegistry } from "./registry.js";
import { SelectionStore } from "./selectionStore.js";
import { serverClock } from "./serverClock.js";
import { SHELL_VERSION } from "./version.js";
/**
 * Bootstrap the Artis SDK on top of a worker bridge. Performs the version handshake,
 * binds the worker's update callback, and wires restart recovery, then returns the
 * {@link Artis} instance whose `v1` surface consumers use.
 *
 * React apps almost always use `<ArtisAppProvider>` instead of calling this directly.
 *
 * @param deps - see {@link CreateArtisDeps}.
 * @returns the live {@link Artis} instance once the worker handshake completes.
 *
 * @example
 * ```ts
 * const artis = await createArtis({ worker: calcsWorkerProxy });
 * const watcher = artis.v1.createWatcher((s) => render(s));
 * watcher.replace([{ id, name: "BRT", field: "value" }]);
 * ```
 */
export const createArtis = async ({ worker, onWorkerReady, selectionStore = new SelectionStore(), layoutStore = new LayoutStore(), productCatalogStore = new ProductCatalogStore(), deploymentStore = new DeploymentStore(""), }) => {
    const workerVersion = await worker.getVersion();
    let registry;
    let scheduled = false;
    // TODO(shell): re-normalises on every add/remove (microtask-coalesced). Fine at tens of subs; throttle or batch behind the scenes if a consumer churns watchers fast.
    const pushSubs = () => {
        if (scheduled)
            return;
        scheduled = true;
        queueMicrotask(() => {
            scheduled = false;
            void worker.setSubscriptions(normalize(registry.allRefs()));
        });
    };
    registry = new WatcherRegistry({ onSubsChange: pushSubs });
    const bindOnUpdate = () => worker.onUpdate(Comlink.proxy((updates) => {
        registry.dispatchUpdates(updates);
    }));
    const eodStore = new EodStore();
    const bindOnEod = () => worker.onEod(Comlink.proxy((snapshot) => eodStore.set(snapshot)));
    // Pages bind on first use, not at bootstrap: the worker starts its pages source (two Hasura
    // subscriptions) on the first bind, and only the pages shell should pay for that.
    const pagesStore = new PagesStore();
    const bindOnPages = () => worker.onPages(Comlink.proxy((delta) => pagesStore.apply(delta)));
    let pagesBound = null;
    const ensurePagesBound = () => {
        pagesBound ??= Promise.resolve(bindOnPages());
        return pagesBound;
    };
    const pagesApi = createPagesApi(pagesStore, async (command) => {
        await ensurePagesBound();
        return worker.pagesCommand(command);
    });
    const pages = {
        ...pagesApi,
        subscribe: (cb, filters) => {
            void ensurePagesBound();
            return pagesApi.subscribe(cb, filters);
        },
        columns: {
            ...pagesApi.columns,
            subscribe: (pageId, cb) => {
                void ensurePagesBound();
                return pagesApi.columns.subscribe(pageId, cb);
            },
        },
    };
    await bindOnUpdate();
    await bindOnEod();
    if (onWorkerReady) {
        onWorkerReady(async () => {
            await worker.setSubscriptions(normalize(registry.allRefs()));
            await bindOnUpdate();
            await bindOnEod();
            if (pagesBound)
                await bindOnPages();
        });
    }
    return {
        v1: {
            version: { app: SHELL_VERSION, worker: workerVersion },
            createWatcher: (onUpdates) => registry.create(onUpdates),
            grid: {
                selection: {
                    subscribe: (cb) => selectionStore.subscribe(cb),
                },
            },
            products: {
                subscribe: (cb, filters) => productCatalogStore.subscribe(cb, filters),
            },
            layout: {
                subscribe: (cb) => layoutStore.subscribe(cb),
                split: (paneId, orientation) => layoutStore.split(paneId, orientation),
                close: (paneId) => layoutStore.close(paneId),
            },
            offsets: {
                validate: (offset) => validateOffset(offset),
                getBaseMonth: () => getBaseMonth(),
                toAbsolute: (offset) => toAbsoluteOffset(offset),
                toRelative: (offset) => toRelativeOffset(offset),
            },
            time: {
                subscribe: (cb) => serverClock.subscribe(cb),
            },
            deployment: {
                subscribe: (cb) => deploymentStore.subscribe(cb),
            },
            eod: {
                subscribe: (cb) => eodStore.subscribe(cb),
            },
            pages,
        },
    };
};
//# sourceMappingURL=createArtis.js.map